// Firebase Realtime Database imports will be loaded dynamically in methods
import { cachedRequest, invalidateCacheByPrefix } from '../lib/requestCache';
import {
  DEFAULT_LEARNING_LANGUAGE_CODE,
  getLearningLanguage,
  normalizeLearningLanguageCode,
} from '../constants/learningLanguages';
import {
  extractCefrLevels,
  formatReaderLevelLabel,
  getPrimaryReaderLevel,
  getReaderLevelRank,
  normalizeCefrLevel,
  normalizeReaderLevelCodes,
  readerSupportsLevel,
} from '../lib/readerLevels';
import {
  CONSISTENCY_MODEL_VERSION,
  CONSISTENCY_START_SCORE,
} from '../lib/consistency';

/**
 * LanguageService - Core service for managing language-specific content and progress
 * This service provides a unified interface for all language-related operations,
 * making it easy to add new languages without changing component code.
 */
// eslint-disable-next-line no-unused-vars
const LANGUAGE_ALIASES = {
  en: 'en',
  english: 'en',
  es: 'es',
  spanish: 'es',
  espanol: 'es',
  fr: 'fr',
  french: 'fr',
  de: 'de',
  german: 'de',
  it: 'it',
  italian: 'it',
  pt: 'pt',
  portuguese: 'pt',
  ja: 'ja',
  japanese: 'ja',
  zh: 'zh',
  chinese: 'zh',
  ko: 'ko',
  korean: 'ko',
  ar: 'ar',
  arabic: 'ar',
  ru: 'ru',
  russian: 'ru',
};

function normalizeLanguageCode(input) {
  return normalizeLearningLanguageCode(input, DEFAULT_LEARNING_LANGUAGE_CODE);
}
function isCefrLevel(level) {
  return Boolean(normalizeCefrLevel(level));
}

function levelMatches(targetLevel, value) {
  if (!targetLevel) return true;
  const target = String(targetLevel).trim().toUpperCase();
  if (!isCefrLevel(target)) return String(value || '').trim().toUpperCase() === target;
  return extractCefrLevels(value).includes(target);
}

function normalizePositiveInteger(value) {
  const parsed = Number(value);
  return Number.isFinite(parsed) && parsed > 0 ? Math.floor(parsed) : null;
}

function normalizeLearningLanguageList(value) {
  if (!Array.isArray(value)) return [];
  return value
    .map((entry) => normalizeLanguageCode(entry))
    .filter((code, index, list) => list.indexOf(code) === index);
}

function shouldUseLegacyLanguageFallback(userData, requestedLanguageCode) {
  if (!userData || typeof userData !== 'object') return false;

  const requestedLanguage = normalizeLanguageCode(requestedLanguageCode);
  const topLevelLanguageRaw = userData.learningLanguage || userData.preferredLearningLanguage;
  const topLevelLanguage = topLevelLanguageRaw
    ? normalizeLanguageCode(topLevelLanguageRaw)
    : null;
  const activeLearningLanguages = normalizeLearningLanguageList(userData.activeLearningLanguages);

  if (topLevelLanguage && topLevelLanguage !== requestedLanguage) {
    return false;
  }

  if (activeLearningLanguages.length > 1) {
    return false;
  }

  return Boolean(
    normalizeCefrLevel(userData.level)
    || normalizePositiveInteger(userData.currentSequence)
    || topLevelLanguage
  );
}

function sanitizeLearningProfilePayload(payload) {
  if (!payload || typeof payload !== 'object') return {};
  const sanitized = {};

  Object.entries(payload).forEach(([key, value]) => {
    if (value !== undefined) {
      sanitized[key] = value;
    }
  });

  const normalizedLevel = normalizeCefrLevel(sanitized.level);
  if (normalizedLevel) {
    sanitized.level = normalizedLevel;
  } else if ('level' in sanitized) {
    delete sanitized.level;
  }

  const normalizedSequence = normalizePositiveInteger(sanitized.currentSequence);
  if (normalizedSequence) {
    sanitized.currentSequence = normalizedSequence;
  } else if ('currentSequence' in sanitized) {
    delete sanitized.currentSequence;
  }

  return sanitized;
}

function getCefrSortRank(value) {
  return getReaderLevelRank(value);
}

function readerMatchesRequestedLevel(readerLevel, requestedLevel) {
  if (!requestedLevel) return true;
  return readerSupportsLevel(readerLevel, requestedLevel);
}

function hasStoriesCollection(readerData) {
  return readerData?.stories !== undefined && readerData?.stories !== null;
}

function countReaderStories(readerData) {
  const stories = readerData?.stories;
  if (Array.isArray(stories)) return stories.length;
  if (stories && typeof stories === 'object') return Object.keys(stories).length;
  if (Number.isFinite(readerData?.storyCount)) return readerData.storyCount;
  return 0;
}

function isPublishedReaderValue(value) {
  return value !== false && value !== 'false';
}

function normalizeReaderSummary(id, readerData, fallbackLanguage) {
  if (!id || !readerData || typeof readerData !== 'object') return null;

  const levelCodes = normalizeReaderLevelCodes([readerData.levelCodes, readerData.level], id);
  const primaryLevel = getPrimaryReaderLevel(levelCodes, id);
  const normalizedLevel = formatReaderLevelLabel(levelCodes, id);
  const normalizedLanguage = typeof readerData.language === 'string' && readerData.language.trim()
    ? readerData.language
    : fallbackLanguage;
  const normalizedTitle = typeof readerData.title === 'string' && readerData.title.trim()
    ? readerData.title.trim()
    : id;

  return {
    id,
    ...readerData,
    title: normalizedTitle,
    level: normalizedLevel,
    levelCodes,
    primaryLevel,
    language: normalizedLanguage,
    storyCount: countReaderStories(readerData),
    published: isPublishedReaderValue(readerData.published),
  };
}

const CACHE_TTL_MS = {
  SHORT: 30 * 1000,
  MEDIUM: 2 * 60 * 1000,
  LONG: 10 * 60 * 1000,
};

const STORAGE_INSTANCE_CACHE = new Map();
const STORAGE_DOWNLOAD_URL_CACHE = new Map();
const EPISODE_ARTWORK_FIELD_CANDIDATES = [
  'artworkUrl',
  'artwork_url',
  'coverImage',
  'cover_image',
  'imageUrl',
  'image_url',
  'image',
  'artwork',
];

function stableSerialize(value) {
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    return JSON.stringify(value ?? null);
  }

  const sortedObject = Object.keys(value)
    .sort()
    .reduce((acc, key) => {
      acc[key] = value[key];
      return acc;
    }, {});

  return JSON.stringify(sortedObject);
}

function encodeStoragePathForPublicUrl(filePath) {
  return encodeURIComponent(filePath).replace(/%2F/g, '/');
}

function isDirectUrl(value) {
  return (
    typeof value === 'string'
    && (
      value.startsWith('http://')
      || value.startsWith('https://')
      || value.startsWith('blob:')
      || value.startsWith('data:')
      || value.startsWith('/')
    )
  );
}

function extractStorageFileInfo(source, languageCode, seriesId) {
  if (typeof source !== 'string') return null;
  const trimmed = source.trim();
  if (!trimmed) return null;

  if (trimmed.startsWith('gs://')) {
    const match = trimmed.match(/^gs:\/\/([^/]+)\/(.+)$/);
    if (match?.[2]) {
      return {
        sourceRef: trimmed,
        bucketName: match[1] || null,
        filePath: decodeURIComponent(match[2]),
      };
    }
  }

  if (trimmed.includes('firebasestorage.googleapis.com')) {
    const match = trimmed.match(/\/b\/([^/]+)\/o\/([^?]+)/);
    if (match?.[2]) {
      return {
        sourceRef: trimmed,
        bucketName: match[1] || null,
        filePath: decodeURIComponent(match[2]),
      };
    }
  }

  if (trimmed.includes('storage.googleapis.com/')) {
    const match = trimmed.match(/storage\.googleapis\.com\/([^/]+)\/([^?]+)/);
    if (match?.[2]) {
      return {
        sourceRef: trimmed,
        bucketName: match[1] || null,
        filePath: decodeURIComponent(match[2]),
      };
    }
  }

  if (/^(gradedReaders|readers|audio|podcasts)\//.test(trimmed)) {
    return {
      sourceRef: trimmed,
      bucketName: null,
      filePath: trimmed,
    };
  }

  if (/^[^/]+\.(mp3|m4a|aac|wav)$/i.test(trimmed) && seriesId) {
    const resolvedPath = `gradedReaders/${languageCode}/${seriesId}/audio/${trimmed}`;
    return {
      sourceRef: resolvedPath,
      bucketName: null,
      filePath: resolvedPath,
    };
  }

  return null;
}

function getEpisodeArtworkCandidate(episode) {
  if (!episode || typeof episode !== 'object') return null;
  for (const fieldName of EPISODE_ARTWORK_FIELD_CANDIDATES) {
    const value = episode[fieldName];
    if (typeof value !== 'string') continue;
    const trimmed = value.trim();
    if (trimmed) return trimmed;
  }
  return null;
}

function normalizeParagraphList(paragraphs) {
  if (Array.isArray(paragraphs)) {
    return paragraphs
      .filter((paragraph) => typeof paragraph === 'string')
      .map((paragraph) => paragraph.trim())
      .filter(Boolean);
  }

  if (paragraphs && typeof paragraphs === 'object') {
    const keys = Object.keys(paragraphs).sort((a, b) => {
      const aNum = Number(a);
      const bNum = Number(b);
      if (Number.isFinite(aNum) && Number.isFinite(bNum)) return aNum - bNum;
      return String(a).localeCompare(String(b));
    });

    return keys
      .map((key) => paragraphs[key])
      .filter((paragraph) => typeof paragraph === 'string')
      .map((paragraph) => paragraph.trim())
      .filter(Boolean);
  }

  return [];
}

function normalizeEpisodeMeta(id, data) {
  if (!id || !data || typeof data !== 'object') return null;

  const description = data.description || data.episode_description || '';

  return {
    id,
    episodeId: data.episodeId || data.episode_id || data.id || id,
    title: data.title || 'Untitled Episode',
    description,
    episode_description: description,
    level: data.level || 'A2',
    topic: data.topic_category || data.topic || 'General',
    thematic_area: data.thematic_area || data.thematicArea || null,
    grammar_topic: data.grammar_topic || data.grammarTopic || null,
    artworkUrl: getEpisodeArtworkCandidate(data),
    published: data.published !== false,
    sequence: data.sequence || data.episodeNumber || 9999,
    episodeNumber: data.episodeNumber || null,
    audioUrl: data.audioUrl || data.audio_url || data.audioPath || data.audio || '',
  };
}

function sortEpisodesBySequence(episodes) {
  return [...episodes].sort((left, right) => {
    const leftSequence = Number(left?.sequence || left?.episodeNumber || 9999);
    const rightSequence = Number(right?.sequence || right?.episodeNumber || 9999);
    return leftSequence - rightSequence;
  });
}

function getCompletedEpisodeIds(progress) {
  return new Set([
    ...(Array.isArray(progress?.completedLessons) ? progress.completedLessons : []),
    ...(Array.isArray(progress?.completedPodcasts) ? progress.completedPodcasts : []),
    ...(Array.isArray(progress?.completedEpisodes) ? progress.completedEpisodes : []),
  ]);
}

function findActiveEpisodeIndex(episodes, progress, fallbackSequence) {
  if (!Array.isArray(episodes) || episodes.length === 0) return -1;

  const completedIds = getCompletedEpisodeIds(progress);
  const lessonProgress = progress?.lessonProgress && typeof progress.lessonProgress === 'object'
    ? progress.lessonProgress
    : {};
  const recentEpisodeIds = Array.isArray(progress?.lastAccessedLessons)
    ? progress.lastAccessedLessons
    : [];

  for (const episodeId of recentEpisodeIds) {
    const progressValue = Number(lessonProgress[episodeId] || 0);
    if (progressValue > 0 && progressValue < 100 && !completedIds.has(episodeId)) {
      const index = episodes.findIndex((episode) => episode.id === episodeId);
      if (index >= 0) return index;
    }
  }

  const nextUncompletedIndex = episodes.findIndex((episode) => !completedIds.has(episode.id));

  const safeFallbackSequence = Number(fallbackSequence || 0);
  if (Number.isFinite(safeFallbackSequence) && safeFallbackSequence > 0) {
    const fallbackIndex = episodes.findIndex((episode) => {
      const episodeSequence = Number(episode.sequence || episode.episodeNumber || 0);
      return episodeSequence >= safeFallbackSequence;
    });

    if (fallbackIndex >= 0) {
      if (nextUncompletedIndex >= 0) {
        return Math.max(fallbackIndex, nextUncompletedIndex);
      }
      return fallbackIndex;
    }
  }

  if (nextUncompletedIndex >= 0) return nextUncompletedIndex;

  return 0;
}

function buildEpisodeWindow(episodes, progress, fallbackSequence, count = 4) {
  const sortedEpisodes = sortEpisodesBySequence(episodes);
  if (sortedEpisodes.length === 0) return [];

  const activeIndex = findActiveEpisodeIndex(sortedEpisodes, progress, fallbackSequence);
  if (activeIndex < 0) return [];

  return sortedEpisodes.slice(activeIndex, activeIndex + Math.max(1, count));
}

export function computeEpisodeProgressionState({
  episodeId,
  episodes,
  progress,
  currentSequence,
  disableProgressionLock = false,
}) {
  const safeEpisodeId = String(episodeId || '').trim();
  if (!safeEpisodeId) {
    return {
      found: false,
      isRevision: false,
      isCurrent: false,
      isProgressionLocked: false,
      activeEpisodeId: null,
    };
  }

  const sortedEpisodes = sortEpisodesBySequence(Array.isArray(episodes) ? episodes : []);
  const completedIds = new Set(
    [...getCompletedEpisodeIds(progress)]
      .map((value) => String(value || '').trim())
      .filter(Boolean)
  );
  const activeIndex = findActiveEpisodeIndex(sortedEpisodes, progress, currentSequence);
  const targetIndex = sortedEpisodes.findIndex(
    (episode) => String(episode?.id || '').trim() === safeEpisodeId
  );
  const activeEpisode = activeIndex >= 0 ? sortedEpisodes[activeIndex] : null;
  const activeEpisodeId = String(activeEpisode?.id || '').trim() || null;
  const isRevision = completedIds.has(safeEpisodeId);
  const isCurrent = !isRevision && activeEpisodeId === safeEpisodeId;
  const isProgressionLocked = !disableProgressionLock
    && !isRevision
    && targetIndex >= 0
    && activeIndex >= 0
    && targetIndex > activeIndex;

  return {
    found: targetIndex >= 0,
    isRevision,
    isCurrent,
    isProgressionLocked,
    activeEpisodeId,
  };
}

async function getReadersSnapshot(readersRef, normalizedLevel, dbOps) {
  const { get } = dbOps;
  return get(readersRef);
}

class LanguageService {
  constructor(languageCode) {
    const normalizedCode = normalizeLanguageCode(languageCode);
    this.lang = normalizedCode;
    this.basePath = `languages/${normalizedCode}`;
    
    // For backward compatibility during migration
    this.isLegacyMode = normalizedCode === 'en';
  }

  getCachePrefix() {
    return `languageService:${this.lang}`;
  }

  getUserCachePrefix(userId) {
    return `${this.getCachePrefix()}:user:${userId}`;
  }

  async resolveStorageAssetUrl(assetSource, options = {}) {
    const trimmedSource = typeof assetSource === 'string' ? assetSource.trim() : '';
    if (!trimmedSource) return null;

    const storageInfo = extractStorageFileInfo(
      trimmedSource,
      options?.languageCode || this.lang,
      options?.seriesId || ''
    );

    if (!storageInfo?.filePath) {
      return trimmedSource;
    }

    const cacheKey = `${storageInfo.bucketName || ''}:${storageInfo.filePath}`;
    if (STORAGE_DOWNLOAD_URL_CACHE.has(cacheKey)) {
      return STORAGE_DOWNLOAD_URL_CACHE.get(cacheKey);
    }

    try {
      const { app, storage } = await import('../firebase');
      const { ref, getDownloadURL, getStorage } = await import('firebase/storage');

      const requestedBucket = storageInfo.bucketName || null;
      const defaultBucket = storage?.app?.options?.storageBucket || null;
      let targetStorage = storage;

      if (requestedBucket && requestedBucket !== defaultBucket) {
        if (!STORAGE_INSTANCE_CACHE.has(requestedBucket)) {
          STORAGE_INSTANCE_CACHE.set(requestedBucket, getStorage(app, `gs://${requestedBucket}`));
        }
        targetStorage = STORAGE_INSTANCE_CACHE.get(requestedBucket);
      }

      if (targetStorage) {
        const resolvedUrl = await getDownloadURL(ref(targetStorage, storageInfo.filePath));
        if (resolvedUrl) {
          STORAGE_DOWNLOAD_URL_CACHE.set(cacheKey, resolvedUrl);
          return resolvedUrl;
        }
      }
    } catch {
      // Fall through to callable/direct URL fallback.
    }

    // Fallback for private non-default buckets (e.g., aprendos-audio-files).
    // Server signs URL from trusted RTDB path; avoids client 400/403 on raw bucket URLs.
    if (options?.episodeId) {
      try {
        const { getEpisodeArtworkAccess } = await import('./audioAccess');
        const response = await getEpisodeArtworkAccess(options.episodeId, options?.languageCode || this.lang);
        const signedArtworkUrl = typeof response?.artworkUrl === 'string' ? response.artworkUrl.trim() : '';
        if (signedArtworkUrl) {
          STORAGE_DOWNLOAD_URL_CACHE.set(cacheKey, signedArtworkUrl);
          return signedArtworkUrl;
        }
      } catch {
        // Fall through to raw/direct URL fallback.
      }
    }

    if (isDirectUrl(trimmedSource)) {
      return trimmedSource;
    }

    if (storageInfo.bucketName) {
      return `https://storage.googleapis.com/${storageInfo.bucketName}/${encodeStoragePathForPublicUrl(storageInfo.filePath)}`;
    }

    return trimmedSource;
  }

  async normalizeEpisodeArtwork(episode) {
    if (!episode || typeof episode !== 'object') return episode;

    const artworkCandidate = getEpisodeArtworkCandidate(episode);
    if (!artworkCandidate) {
      return {
        ...episode,
        artworkUrl: episode.artworkUrl || null,
      };
    }

    const episodeId = String(episode.id || episode.episodeId || '').trim();
    const resolvedArtworkUrl = await this.resolveStorageAssetUrl(artworkCandidate, {
      episodeId: episodeId || null,
      languageCode: this.lang,
    });
    return {
      ...episode,
      artworkUrl: resolvedArtworkUrl || artworkCandidate,
    };
  }

  async hydrateEpisodeArtwork(episodes) {
    return Promise.all(
      (Array.isArray(episodes) ? episodes : []).map((episode) => this.normalizeEpisodeArtwork(episode))
    );
  }

  /**
   * Check if new structure exists (for migration period)
   */
  async checkNewStructureExists() {
    const cacheKey = `${this.getCachePrefix()}:checkNewStructureExists`;
    return cachedRequest(cacheKey, async () => {
      try {
        const { rtdb } = await import('../firebase');
        const { ref, get } = await import('firebase/database');
        const configRef = ref(rtdb, `${this.basePath}/config`);
        const snapshot = await get(configRef);
        return snapshot.exists();
      } catch {
        return false;
      }
    }, { ttlMs: CACHE_TTL_MS.LONG });
  }

  // ============= CONFIG METHODS =============
  
  /**
   * Get language configuration
   */
  async getConfig() {
    if (this.isLegacyMode) {
      const legacyLanguage = getLearningLanguage('en');
      return {
        name: legacyLanguage.name,
        code: legacyLanguage.code,
        active: true,
        levels: legacyLanguage.levels || ['A1', 'A2', 'B1', 'B2', 'C1', 'C2'],
        features: {
          placementTest: Boolean(legacyLanguage.features?.placementTest),
          spacedRepetition: true,
          certificates: true
        }
      };
    }

    const cacheKey = `${this.getCachePrefix()}:config`;
    return cachedRequest(cacheKey, async () => {
      const { rtdb } = await import('../firebase');
      const { ref, get } = await import('firebase/database');
      const configRef = ref(rtdb, `${this.basePath}/config`);
      const snapshot = await get(configRef);
      return snapshot.val();
    }, { ttlMs: CACHE_TTL_MS.LONG });
  }

  // ============= CONTENT METHODS =============
  
  /**
   * Get podcasts with optional filters
   */
  async getPodcasts(filters = {}) {
    const podcasts = await this.getPodcastMetadata(filters);
    return this.hydrateEpisodeArtwork(podcasts);
  }

  async getPodcastMetadata(filters = {}) {
    const cacheKey = `${this.getCachePrefix()}:v2:podcastsMeta:${stableSerialize(filters)}`;
    return cachedRequest(cacheKey, async () => {
      const { rtdb } = await import('../firebase');
      const { ref, get, query, orderByChild, equalTo } = await import('firebase/database');

      // Use consistent structure for all languages
      const dataPath = `languages/${this.lang}/content/episodes`;

      try {
        const podcastsRef = ref(rtdb, dataPath);
        const normalizedLevel = typeof filters.level === 'string' ? filters.level.trim().toUpperCase() : '';
        const hasCefrLevel = isCefrLevel(normalizedLevel);
        const podcastsQuery = hasCefrLevel
          ? query(podcastsRef, orderByChild('level'), equalTo(normalizedLevel))
          : podcastsRef;
        let snapshot;
        try {
          snapshot = await get(podcastsQuery);
        } catch (queryError) {
          console.warn(`[LanguageService] Level query failed at ${dataPath}, falling back to full read:`, queryError);
          snapshot = null;
        }

        // Fallback for mixed-case/legacy level values: full read + normalized filter.
        if (!snapshot || (hasCefrLevel && !snapshot.exists())) {
          snapshot = await get(podcastsRef);
        }

        if (!snapshot.exists()) {
          return [];
        }

        const data = snapshot.val();
        let podcasts = Object.keys(data)
          .map((key) => normalizeEpisodeMeta(key, data[key]))
          .filter((episode) => episode && episode.title && episode.audioUrl);

        // Apply filters
        if (hasCefrLevel) {
          podcasts = podcasts.filter((episode) => levelMatches(normalizedLevel, episode.level));
        } else if (filters.level) {
          podcasts = podcasts.filter((episode) => episode.level === filters.level);
        }
        if (filters.topic) {
          podcasts = podcasts.filter((episode) => episode.topic === filters.topic);
        }
        if (filters.published !== undefined) {
          podcasts = podcasts.filter((episode) => episode.published === filters.published);
        }

        podcasts = sortEpisodesBySequence(podcasts);

        if (filters.limit) {
          podcasts = podcasts.slice(0, filters.limit);
        }
        return podcasts;
      } catch (error) {
        console.error(`Error fetching from ${dataPath}:`, error);
        return [];
      }
    }, { ttlMs: CACHE_TTL_MS.MEDIUM });
  }

  /**
   * Get episodes (alias for getPodcasts for backward compatibility)
   */
  async getEpisodes(filters = {}) {
    return this.getPodcasts(filters);
  }

  async resolveEpisodeWindowContext(userId, options = {}) {
    const resolvedLevel = options.level || (userId ? await this.resolveUserLevel(userId) : 'A2');

    let resolvedProgress = options.progress || null;
    let resolvedCurrentSequence = options.currentSequence ?? null;

    if (userId && (!resolvedProgress || resolvedCurrentSequence == null)) {
      const [appProgress, profile] = await Promise.all([
        resolvedProgress ? Promise.resolve(resolvedProgress) : this.getUserProgressFromAppStructure(userId).catch(() => null),
        resolvedCurrentSequence ? Promise.resolve({ currentSequence: resolvedCurrentSequence }) : this.getUserProfile(userId).catch(() => null),
      ]);

      if (!resolvedProgress) {
        resolvedProgress = appProgress;
      }
      if (!resolvedCurrentSequence) {
        resolvedCurrentSequence = profile?.currentSequence || null;
      }
    }

    return {
      level: resolvedLevel,
      progress: resolvedProgress,
      currentSequence: resolvedCurrentSequence,
    };
  }

  async getPodcastWindow(options = {}) {
    const {
      userId = null,
      count = 4,
      published = true,
      topic,
    } = options;
    const context = await this.resolveEpisodeWindowContext(userId, options);
    const episodes = await this.getPodcastMetadata({
      level: context.level,
      published,
      ...(topic ? { topic } : {}),
    });
    const windowEpisodes = buildEpisodeWindow(
      episodes,
      context.progress,
      context.currentSequence,
      count
    );
    return this.hydrateEpisodeArtwork(windowEpisodes);
  }

  async getEpisodeProgressionState(userId, episodeId, options = {}) {
    const safeEpisodeId = String(episodeId || '').trim();
    if (!safeEpisodeId) {
      return {
        found: false,
        isRevision: false,
        isCurrent: false,
        isProgressionLocked: false,
        activeEpisodeId: null,
      };
    }

    const context = await this.resolveEpisodeWindowContext(userId, options);
    const episodes = await this.getPodcastMetadata({
      level: context.level,
      published: options.published !== undefined ? options.published : true,
    });
    return computeEpisodeProgressionState({
      episodeId: safeEpisodeId,
      episodes,
      progress: context.progress,
      currentSequence: context.currentSequence,
      disableProgressionLock: options.disableProgressionLock === true,
    });
  }

  async getNextPodcast(userId, options = {}) {
    const context = await this.resolveEpisodeWindowContext(userId, options);
    let episodes = await this.getPodcastMetadata({
      level: context.level,
      published: options.published !== undefined ? options.published : true,
    });

    if (!episodes.length) {
      episodes = await this.getPodcastMetadata({
        published: options.published !== undefined ? options.published : true,
      });
    }

    const activeEpisode = buildEpisodeWindow(
      episodes,
      context.progress,
      context.currentSequence,
      1
    )[0] || null;

    if (!activeEpisode?.id) {
      return null;
    }

    try {
      return await this.getPodcast(activeEpisode.id);
    } catch {
      const [hydratedEpisode] = await this.hydrateEpisodeArtwork([activeEpisode]);
      return hydratedEpisode || activeEpisode;
    }
  }

  /**
   * Get single podcast by ID
   */
  async getPodcast(podcastId) {
    const cacheKey = `${this.getCachePrefix()}:v2:podcast:${podcastId}`;
    return cachedRequest(cacheKey, async () => {
      const { rtdb } = await import('../firebase');
      const { ref, get } = await import('firebase/database');

      // Use consistent structure for all languages
      const dataPath = `languages/${this.lang}/content/episodes/${podcastId}`;

      const podcastRef = ref(rtdb, dataPath);
      const snapshot = await get(podcastRef);

      if (!snapshot.exists()) {
        console.error(`Podcast not found at path: ${dataPath}`);
        const notFound = new Error(`Podcast ${podcastId} not found`);
        // Lets pages tell a missing episode apart from a failed request.
        notFound.code = 'episode-not-found';
        throw notFound;
      }

      const podcast = {
        id: podcastId,
        ...snapshot.val()
      };
      return this.normalizeEpisodeArtwork(podcast);
    }, { ttlMs: CACHE_TTL_MS.MEDIUM });
  }

  /**
   * Get exercises for a podcast
   */
  async getExercises(podcastId) {
    const cacheKey = `${this.getCachePrefix()}:exercises:${podcastId}`;
    return cachedRequest(cacheKey, async () => {
      const { rtdb } = await import('../firebase');
      const { ref, get } = await import('firebase/database');

      let dataPath;

      if (this.lang === 'en') {
        // For English, use the main exercises collection
        dataPath = 'exercises';
      } else {
        // For Spanish and other languages, use the languages structure
        dataPath = `languages/${this.lang}/content/exercises`;
      }

      try {
        const exercisesRef = ref(rtdb, dataPath);
        const snapshot = await get(exercisesRef);

        if (!snapshot.exists()) {
          return [];
        }

        const data = snapshot.val();
        const exercises = Object.keys(data)
          .map(key => ({
            id: key,
            ...data[key]
          }))
          .filter(ex => ex.episodeId === podcastId || ex.podcastId === podcastId)
          .sort((a, b) => (a.order || 0) - (b.order || 0));

        return exercises;
      } catch (error) {
        console.error(`Error fetching exercises from ${dataPath}:`, error);
        return [];
      }
    }, { ttlMs: CACHE_TTL_MS.MEDIUM });
  }

  // ============= USER PROGRESS METHODS =============
  
  /**
   * Get user's learning profile for this language
   */
  async getUserProfile(userId) {
    const cacheKey = `${this.getUserCachePrefix(userId)}:profile`;
    return cachedRequest(cacheKey, async () => {
      const { db, rtdb } = await import('../firebase');
      const { doc, getDoc } = await import('firebase/firestore');
      const { ref, get } = await import('firebase/database');

      const scopedProfileRef = doc(db, 'users', userId, 'learning', this.lang, 'profile', 'profile');
      const scopedProfileSnapshot = await getDoc(scopedProfileRef);

      if (scopedProfileSnapshot.exists()) {
        return sanitizeLearningProfilePayload(scopedProfileSnapshot.data() || {});
      }

      const profilePath = `users/${userId}/learning/${this.lang}/profile`;
      const profileRef = ref(rtdb, profilePath);
      const snapshot = await get(profileRef);

      if (snapshot.exists()) {
        const rtdbProfile = sanitizeLearningProfilePayload(snapshot.val() || {});
        if (Object.keys(rtdbProfile).length > 0) {
          return rtdbProfile;
        }
      }

      const appProfile = await this.getUserProfileFromAppStructure(userId).catch(() => null);
      if (shouldUseLegacyLanguageFallback(appProfile, this.lang)) {
        const fallbackProfile = sanitizeLearningProfilePayload({
          level: appProfile?.level,
          currentSequence: appProfile?.currentSequence,
        });
        if (Object.keys(fallbackProfile).length > 0) {
          return fallbackProfile;
        }
      }

      return {};
    }, { ttlMs: CACHE_TTL_MS.SHORT });
  }

  /**
   * Update user's learning profile
   */
  async updateUserProfile(userId, updates) {
    const sanitizedUpdates = sanitizeLearningProfilePayload(updates);
    if (Object.keys(sanitizedUpdates).length === 0) {
      return;
    }

    const { db, rtdb } = await import('../firebase');
    const { doc, setDoc } = await import('firebase/firestore');
    const { ref, update } = await import('firebase/database');

    const scopedProfileRef = doc(db, 'users', userId, 'learning', this.lang, 'profile', 'profile');
    await setDoc(scopedProfileRef, {
      ...sanitizedUpdates,
      language: this.lang,
      updatedAt: new Date(),
    }, { merge: true });

    const profilePath = `users/${userId}/learning/${this.lang}/profile`;
    const profileRef = ref(rtdb, profilePath);
    try {
      await update(profileRef, sanitizedUpdates);
    } catch (error) {
      console.warn('[LanguageService] Failed to sync learning profile to RTDB:', error);
    }

    invalidateCacheByPrefix(this.getUserCachePrefix(userId));
    invalidateCacheByPrefix(`languageService:app:user:${userId}`);
  }

  /**
   * Get user's progress data
   */
  async getUserProgress(userId) {
    const cacheKey = `${this.getUserCachePrefix(userId)}:progress`;
    return cachedRequest(cacheKey, async () => {
      const { rtdb } = await import('../firebase');
      const { ref, get, set } = await import('firebase/database');

      const progressPath = `users/${userId}/learning/${this.lang}/progress`;
      const progressRef = ref(rtdb, progressPath);
      const snapshot = await get(progressRef);

      if (!snapshot.exists()) {
        // Initialize progress if it doesn't exist
        const initialProgress = {
          completedPodcasts: [],
          completedExercises: [],
          totalMinutes: 0,
          currentStreak: 0,
          longestStreak: 0,
          lastActivityDate: null,
          points: 0,
          weeklyMinutes: {}
        };

        await set(progressRef, initialProgress);
        return initialProgress;
      }

      return snapshot.val();
    }, { ttlMs: CACHE_TTL_MS.SHORT });
  }

  /**
   * Update user's progress
   */
  async updateProgress(userId, updates) {
    const { rtdb } = await import('../firebase');
    const { ref, update, serverTimestamp } = await import('firebase/database');

    const progressPath = `users/${userId}/learning/${this.lang}/progress`;
    const progressRef = ref(rtdb, progressPath);
    await update(progressRef, {
      ...updates,
      lastActivityDate: serverTimestamp()
    });
    invalidateCacheByPrefix(this.getUserCachePrefix(userId));
    invalidateCacheByPrefix(`languageService:app:user:${userId}`);
  }

  /**
   * Resolve the user's CEFR level for the active learning language.
   * Prefer language-scoped profile/progress and only fall back to the legacy
   * global user document when the account is effectively single-language.
   */
  async resolveUserLevel(userId) {
    const cacheKey = `${this.getUserCachePrefix(userId)}:resolvedLevel`;
    return cachedRequest(cacheKey, async () => {
      if (!userId) return 'A2';

      const [profile, progress, appProfile, appProgress] = await Promise.all([
        this.getUserProfile(userId).catch(() => null),
        this.getUserProgress(userId).catch(() => null),
        this.getUserProfileFromAppStructure(userId).catch(() => null),
        this.getUserProgressFromAppStructure(userId).catch(() => null),
      ]);

      const scopedLevel = normalizeCefrLevel(profile?.level)
        || normalizeCefrLevel(progress?.level);
      if (scopedLevel) {
        return scopedLevel;
      }

      const canUseGlobalFallback = shouldUseLegacyLanguageFallback({
        learningLanguage: appProfile?.learningLanguage,
        preferredLearningLanguage: appProfile?.preferredLearningLanguage,
        activeLearningLanguages: appProfile?.activeLearningLanguages,
        level: appProfile?.level || appProgress?.level,
        currentSequence: appProfile?.currentSequence,
      }, this.lang);
      const fallbackLevel = canUseGlobalFallback
        ? (normalizeCefrLevel(appProfile?.level) || normalizeCefrLevel(appProgress?.level))
        : null;

      if (fallbackLevel) {
        try {
          await this.updateUserProfile(userId, { level: fallbackLevel });
        } catch {
          void 0;
        }
        return fallbackLevel;
      }

      return 'A2';
    }, { ttlMs: CACHE_TTL_MS.SHORT });
  }

  /**
   * Get user progress from app's flat Firestore structure
   * This reads from the same location the native app uses for cross-platform sync
   * @param {string} userId - User ID
   * @returns {Promise<Object>} User progress data from app structure
   */
  async getUserProgressFromAppStructure(userId) {
    const cacheKey = `languageService:app:user:${userId}:progress`;
    return cachedRequest(cacheKey, async () => {
      const { db } = await import('../firebase');
      const { doc, getDoc } = await import('firebase/firestore');

      try {
        const userDoc = await getDoc(doc(db, 'users', userId));

        if (!userDoc.exists()) {
          return {
            completedLessons: [],
            lessonProgress: {},
            lastAccessedLessons: [],
            continueLearning: {},
            currentStreak: 0,
            longestStreak: 0,
            totalListeningSeconds: 0,
            level: null,
            unlockedBadges: [],
            activityLog: {},
            consistencyScore: CONSISTENCY_START_SCORE,
            consistencyLastEvaluatedWeek: '',
            consistencyModelVersion: CONSISTENCY_MODEL_VERSION,
          };
        }

        const data = userDoc.data();
        return {
          completedLessons: data.completedLessons || [],
          lessonProgress: data.lessonProgress || {},
          lastAccessedLessons: data.lastAccessedLessons || [],
          continueLearning: data.continueLearning || {},
          currentStreak: data.currentStreak || 0,
          longestStreak: data.longestStreak || 0,
          totalListeningSeconds: data.totalListeningSeconds || 0,
          level: normalizeCefrLevel(data.level),
          unlockedBadges: data.unlockedBadges || [],
          activityLog: data.activityLog || {},
          lastActiveDate: data.lastActiveDate || null,
          consistencyScore: Number.isFinite(Number(data.consistencyScore))
            ? Number(data.consistencyScore)
            : CONSISTENCY_START_SCORE,
          consistencyLastEvaluatedWeek: typeof data.consistencyLastEvaluatedWeek === 'string'
            ? data.consistencyLastEvaluatedWeek
            : '',
          consistencyModelVersion: Number.isFinite(Number(data.consistencyModelVersion))
            ? Number(data.consistencyModelVersion)
            : CONSISTENCY_MODEL_VERSION,
          activeLearningLanguages: Array.isArray(data.activeLearningLanguages)
            ? data.activeLearningLanguages.map((code) => normalizeLanguageCode(code))
            : [],
        };
      } catch (error) {
        console.error('Error fetching user progress from app structure:', error);
        return {
          completedLessons: [],
          lessonProgress: {},
          lastAccessedLessons: [],
          continueLearning: {},
          currentStreak: 0,
          longestStreak: 0,
          totalListeningSeconds: 0,
          level: null,
          unlockedBadges: [],
          activityLog: {},
          consistencyScore: CONSISTENCY_START_SCORE,
          consistencyLastEvaluatedWeek: '',
          consistencyModelVersion: CONSISTENCY_MODEL_VERSION,
        };
      }
    }, { ttlMs: CACHE_TTL_MS.SHORT });
  }

  /**
   * Get user profile from app's flat Firestore structure
   * This reads from the same location the native app uses for cross-platform sync
   * @param {string} userId - User ID
   * @returns {Promise<Object>} User profile data from app structure
   */
  async getUserProfileFromAppStructure(userId) {
    const cacheKey = `languageService:app:user:${userId}:profile`;
    return cachedRequest(cacheKey, async () => {
      const { db } = await import('../firebase');
      const { doc, getDoc } = await import('firebase/firestore');

      try {
        const userDoc = await getDoc(doc(db, 'users', userId));

        if (!userDoc.exists()) {
          return null;
        }

        const data = userDoc.data();
        return {
          displayName: data.displayName || null,
          email: data.email || null,
          photoURL: data.photoURL || null,
          level: normalizeCefrLevel(data.level),
          learningLanguage: data.learningLanguage ? normalizeLanguageCode(data.learningLanguage) : null,
          preferredLearningLanguage: data.preferredLearningLanguage
            ? normalizeLanguageCode(data.preferredLearningLanguage)
            : (data.learningLanguage ? normalizeLanguageCode(data.learningLanguage) : null),
          activeLearningLanguages: Array.isArray(data.activeLearningLanguages)
            ? data.activeLearningLanguages.map((code) => normalizeLanguageCode(code))
            : [],
          onboardingCompleted: data.onboardingCompleted || false,
          currentSequence: normalizePositiveInteger(data.currentSequence)
        };
      } catch (error) {
        console.error('Error fetching user profile from app structure:', error);
        return null;
      }
    }, { ttlMs: CACHE_TTL_MS.SHORT });
  }

  // ============= GRADED READERS - PRODUCT CATALOG =============

  /**
   * Get all graded readers for current language with optional filters
   * @param {Object} filters - { level: 'A2', published: true, featured: false, minPrice: 0, maxPrice: 20 }
   * @returns {Promise<Array>} Array of graded reader objects
   */
  async getGradedReaders(filters = {}) {
    const cacheKey = `${this.getCachePrefix()}:gradedReaders:${stableSerialize(filters)}`;
    return cachedRequest(cacheKey, async () => {
      const { rtdb } = await import('../firebase');
      const { ref, get, query, orderByChild, equalTo } = await import('firebase/database');

      const dataPath = `languages/${this.lang}/content/gradedReaders`;

      try {
        const readersRef = ref(rtdb, dataPath);
        const normalizedLevel = normalizeCefrLevel(filters.level);
        const snapshot = await getReadersSnapshot(
          readersRef,
          normalizedLevel,
          { get, query, orderByChild, equalTo }
        );

        if (!snapshot.exists()) {
          return [];
        }

        const readers = [];
        snapshot.forEach((childSnapshot) => {
          try {
            const readerId = childSnapshot.key;
            const readerData = childSnapshot.val();
            if (!readerId || !readerData || typeof readerData !== 'object') return;
            if (!hasStoriesCollection(readerData)) return;

            const reader = normalizeReaderSummary(readerId, readerData, this.lang);
            if (!reader) return;
            if (!readerMatchesRequestedLevel(reader.levelCodes || reader.level, normalizedLevel)) return;

            if (filters.level && !normalizedLevel) {
              if (!readerSupportsLevel(reader.levelCodes || reader.level, filters.level, readerId)) return;
            }

            if (filters.published !== undefined) {
              const isPublished = reader.published === true;
              if (filters.published === true && !isPublished) return;
              if (filters.published === false && isPublished) return;
            }

            if (filters.featured !== undefined && Boolean(reader.featured) !== filters.featured) return;

            const price = Number(reader.price ?? 0);
            if (filters.minPrice !== undefined && price < filters.minPrice) return;
            if (filters.maxPrice !== undefined && price > filters.maxPrice) return;

            readers.push(reader);
          } catch (itemError) {
            console.warn(
              `[LanguageService] Skipping malformed reader record at ${dataPath}/${childSnapshot.key}:`,
              itemError
            );
          }
        });

        // Sort: featured first, then by level, then by title
        readers.sort((a, b) => {
          if (a.featured && !b.featured) return -1;
          if (!a.featured && b.featured) return 1;
          const rankDiff = getCefrSortRank(a.levelCodes || a.level) - getCefrSortRank(b.levelCodes || b.level);
          if (rankDiff !== 0) return rankDiff;
          return String(a.title || a.id).localeCompare(String(b.title || b.id));
        });

        if (filters.limit) {
          return readers.slice(0, filters.limit);
        }

        return readers;
      } catch (error) {
        console.error(`Error fetching graded readers from ${dataPath}:`, error);
        return [];
      }
    }, { ttlMs: CACHE_TTL_MS.MEDIUM });
  }

  /**
   * Get single graded reader with full details
   * @param {string} readerId - The reader ID (e.g., 'en-a2-mystery')
   * @returns {Promise<Object>} Full reader object with chapters, vocabulary, etc.
   */
  async getGradedReader(readerId) {
    const cacheKey = `${this.getCachePrefix()}:gradedReader:${readerId}`;
    return cachedRequest(cacheKey, async () => {
      const { rtdb } = await import('../firebase');
      const { ref, get } = await import('firebase/database');

      const dataPath = `languages/${this.lang}/content/gradedReaders/${readerId}`;
      const readerRef = ref(rtdb, dataPath);
      const snapshot = await get(readerRef);

      if (!snapshot.exists()) {
        console.error(`Graded reader not found at path: ${dataPath}`);
        throw new Error(`Graded reader ${readerId} not found`);
      }

      return {
        id: readerId,
        ...snapshot.val()
      };
    }, { ttlMs: CACHE_TTL_MS.MEDIUM });
  }

  /**
   * Get all chapters for a graded reader
   * @param {string} readerId
   * @returns {Promise<Array>} Array of chapter objects with audio URLs
   */
  async getReaderChapters(readerId) {
    const cacheKey = `${this.getCachePrefix()}:readerChapters:${readerId}`;
    return cachedRequest(cacheKey, async () => {
      const { rtdb } = await import('../firebase');
      const { ref, get } = await import('firebase/database');

      const chaptersPath = `languages/${this.lang}/content/gradedReaders/${readerId}/chapters`;
      const chaptersRef = ref(rtdb, chaptersPath);
      const snapshot = await get(chaptersRef);

      if (!snapshot.exists()) {
        return [];
      }

      const chapters = snapshot.val();
      return Array.isArray(chapters) ? chapters : Object.values(chapters);
    }, { ttlMs: CACHE_TTL_MS.MEDIUM });
  }

  /**
   * Get featured graded readers for homepage/store
   * @param {number} limit - Max number to return
   * @returns {Promise<Array>}
   */
  async getFeaturedGradedReaders(limit = 3) {
    return this.getGradedReaders({ featured: true, published: true, limit });
  }

  // ============= GRADED READERS - USER PURCHASES & ACCESS =============

  /**
   * Check if user owns a specific graded reader
   * @param {string} userId
   * @param {string} readerId
   * @returns {Promise<boolean>}
   */
  async hasReaderAccess(userId, readerId) {
    const cacheKey = `${this.getUserCachePrefix(userId)}:hasReaderAccess:${readerId}`;
    return cachedRequest(cacheKey, async () => {
      const { db } = await import('../firebase');
      const { collection, query, where, getDocs } = await import('firebase/firestore');

      try {
        const purchasesRef = collection(db, 'users', userId, 'purchases');
        const q = query(
          purchasesRef,
          where('productType', '==', 'gradedReader'),
          where('readerId', '==', readerId),
          where('accessGranted', '==', true)
        );

        const snapshot = await getDocs(q);
        return !snapshot.empty;
      } catch (error) {
        console.error('Error checking reader access:', error);
        return false;
      }
    }, { ttlMs: CACHE_TTL_MS.SHORT });
  }

  /**
   * Get all user's purchased graded readers
   * @param {string} userId
   * @returns {Promise<Array>} Array of purchase objects with reader details
   */
  async getUserPurchases(userId) {
    const cacheKey = `${this.getUserCachePrefix(userId)}:purchases`;
    return cachedRequest(cacheKey, async () => {
      const { db } = await import('../firebase');
      const { collection, query, where, getDocs, orderBy } = await import('firebase/firestore');

      try {
        const purchasesRef = collection(db, 'users', userId, 'purchases');
        const q = query(
          purchasesRef,
          where('productType', '==', 'gradedReader'),
          where('language', '==', this.lang),
          orderBy('purchasedAt', 'desc')
        );

        const snapshot = await getDocs(q);
        const purchases = [];

        for (const doc of snapshot.docs) {
          const purchaseData = { id: doc.id, ...doc.data() };

          // Enrich with reader metadata
          try {
            const reader = await this.getGradedReader(purchaseData.readerId);
            purchaseData.readerDetails = reader;
          } catch {
            console.warn(`Could not fetch reader details for ${purchaseData.readerId}`);
          }

          purchases.push(purchaseData);
        }

        return purchases;
      } catch (error) {
        console.error('Error fetching user purchases:', error);
        return [];
      }
    }, { ttlMs: CACHE_TTL_MS.SHORT });
  }

  /**
   * Record a purchase after payment webhook confirms
   * @param {string} userId
   * @param {Object} purchaseData
   * @returns {Promise<string>} Purchase ID
   */
  async recordPurchase(userId, purchaseData) {
    const { db } = await import('../firebase');
    const { collection, addDoc, serverTimestamp } = await import('firebase/firestore');

    const purchasesRef = collection(db, 'users', userId, 'purchases');
    const docRef = await addDoc(purchasesRef, {
      ...purchaseData,
      purchasedAt: serverTimestamp(),
      createdAt: serverTimestamp()
    });

    invalidateCacheByPrefix(this.getUserCachePrefix(userId));
    return docRef.id;
  }

  // ============= GRADED READERS - READING PROGRESS =============

  /**
   * Get user's reading progress for a specific reader
   * @param {string} userId
   * @param {string} readerId
   * @returns {Promise<Object|null>} Progress object or null if not started
   */
  async getReadingProgress(userId, readerId) {
    const cacheKey = `${this.getUserCachePrefix(userId)}:readingProgress:${readerId}`;
    return cachedRequest(cacheKey, async () => {
      const { db } = await import('../firebase');
      const { doc, getDoc } = await import('firebase/firestore');

      try {
        const progressRef = doc(db, 'users', userId, 'readingProgress', readerId);
        const snapshot = await getDoc(progressRef);

        if (!snapshot.exists()) {
          return null;
        }

        return {
          id: snapshot.id,
          ...snapshot.data()
        };
      } catch (error) {
        console.error('Error fetching reading progress:', error);
        return null;
      }
    }, { ttlMs: CACHE_TTL_MS.SHORT });
  }

  /**
   * Update reading progress (auto-save while reading)
   * @param {string} userId
   * @param {string} readerId
   * @param {Object} progressData - { currentChapter, currentPosition, audioPosition, etc. }
   * @returns {Promise<void>}
   */
  async updateReadingProgress(userId, readerId, progressData) {
    const { db } = await import('../firebase');
    const { doc, setDoc, serverTimestamp } = await import('firebase/firestore');

    const progressRef = doc(db, 'users', userId, 'readingProgress', readerId);

    // Calculate progress percentage if total chapters provided
    if (progressData.totalChapters && progressData.completedChapters) {
      progressData.progressPercentage = Math.round(
        (progressData.completedChapters.length / progressData.totalChapters) * 100
      );
    }

    await setDoc(progressRef, {
      readerId,
      language: this.lang,
      ...progressData,
      lastReadAt: serverTimestamp(),
      updatedAt: serverTimestamp()
    }, { merge: true });

    invalidateCacheByPrefix(this.getUserCachePrefix(userId));
  }

  /**
   * Add bookmark to reader
   * @param {string} userId
   * @param {string} readerId
   * @param {Object} bookmark - { chapterId, position, note }
   * @returns {Promise<void>}
   */
  async addBookmark(userId, readerId, bookmark) {
    const { db } = await import('../firebase');
    const { doc, updateDoc, arrayUnion, serverTimestamp } = await import('firebase/firestore');

    const progressRef = doc(db, 'users', userId, 'readingProgress', readerId);

    const bookmarkWithTimestamp = {
      ...bookmark,
      createdAt: Date.now()
    };

    await updateDoc(progressRef, {
      bookmarks: arrayUnion(bookmarkWithTimestamp),
      updatedAt: serverTimestamp()
    });

    invalidateCacheByPrefix(this.getUserCachePrefix(userId));
  }

  /**
   * Remove bookmark from reader
   * @param {string} userId
   * @param {string} readerId
   * @param {Object} bookmark - The exact bookmark object to remove
   * @returns {Promise<void>}
   */
  async removeBookmark(userId, readerId, bookmark) {
    const { db } = await import('../firebase');
    const { doc, updateDoc, arrayRemove, serverTimestamp } = await import('firebase/firestore');

    const progressRef = doc(db, 'users', userId, 'readingProgress', readerId);

    await updateDoc(progressRef, {
      bookmarks: arrayRemove(bookmark),
      updatedAt: serverTimestamp()
    });

    invalidateCacheByPrefix(this.getUserCachePrefix(userId));
  }

  /**
   * Mark chapter as completed
   * @param {string} userId
   * @param {string} readerId
   * @param {number} chapterId
   * @returns {Promise<void>}
   */
  async completeChapter(userId, readerId, chapterId) {
    const { db } = await import('../firebase');
    const { doc, updateDoc, arrayUnion, getDoc, serverTimestamp } = await import('firebase/firestore');

    const progressRef = doc(db, 'users', userId, 'readingProgress', readerId);
    const progressSnap = await getDoc(progressRef);

    if (!progressSnap.exists()) {
      console.error('Reading progress not found');
      return;
    }

    const progress = progressSnap.data();
    const completedChapters = progress.completedChapters || [];

    // Don't add if already completed
    if (completedChapters.includes(chapterId)) {
      return;
    }

    const updatedCompleted = [...completedChapters, chapterId];
    const totalChapters = progress.totalChapters || 0;
    const progressPercentage = totalChapters > 0
      ? Math.round((updatedCompleted.length / totalChapters) * 100)
      : 0;

    const updateData = {
      completedChapters: arrayUnion(chapterId),
      progressPercentage,
      updatedAt: serverTimestamp()
    };

    // If all chapters complete, mark as finished
    if (totalChapters > 0 && updatedCompleted.length >= totalChapters) {
      updateData.completedAt = serverTimestamp();
    }

    await updateDoc(progressRef, updateData);
    invalidateCacheByPrefix(this.getUserCachePrefix(userId));
  }

  // ============= GRADED READERS - SERIES/STORY STRUCTURE =============

  /**
   * Get a graded reader series (collection of stories)
   * @param {string} seriesId - e.g., 'es-a2-historias'
   * @returns {Promise<Object>} Series metadata
   */
  async getReaderSeries(seriesId) {
    const cacheKey = `${this.getCachePrefix()}:readerSeries:${seriesId}:v2`;
    return cachedRequest(cacheKey, async () => {
      const { rtdb } = await import('../firebase');
      const { ref, get } = await import('firebase/database');

      const fallbackLanguage = normalizeLanguageCode(seriesId?.split('-')?.[0] || '');
      const candidateLanguages = Array.from(
        new Set([this.lang, fallbackLanguage].filter((value) => typeof value === 'string' && value.trim()))
      );

      let snapshot = null;
      let dataPath = '';
      for (const languageCode of candidateLanguages) {
        dataPath = `languages/${languageCode}/content/gradedReaders/${seriesId}`;
        const seriesRef = ref(rtdb, dataPath);
        const candidateSnapshot = await get(seriesRef);
        if (candidateSnapshot.exists()) {
          snapshot = candidateSnapshot;
          break;
        }
      }

      if (!snapshot?.exists()) {
        console.error(`Series not found at path: ${dataPath}`);
        return null;
      }

      const data = snapshot.val();
      // Don't include stories array in the main object
      const { stories, ...seriesData } = data;

      // Stories can be an array or object - handle both
      let storyCount = 0;
      if (Array.isArray(stories)) {
        storyCount = stories.length;
      } else if (stories && typeof stories === 'object') {
        storyCount = Object.keys(stories).length;
      }

      const normalizedSeries = normalizeReaderSummary(
        seriesId,
        seriesData,
        seriesData?.language || fallbackLanguage || this.lang
      );

      if (!normalizedSeries) return null;

      return {
        ...normalizedSeries,
        storyCount,
      };
    }, { ttlMs: CACHE_TTL_MS.MEDIUM });
  }

  /**
   * Get all stories in a series
   * Stories are stored as an array with each story having its own 'id' field
   * @param {string} seriesId - e.g., 'es-a2-historias'
   * @returns {Promise<Array>} Array of story objects sorted by order/index
   */
  async getSeriesStories(seriesId) {
    const cacheKey = `${this.getCachePrefix()}:seriesStories:${seriesId}:v3`;
    return cachedRequest(cacheKey, async () => {
      const { rtdb } = await import('../firebase');
      const { ref, get } = await import('firebase/database');

      const fallbackLanguage = normalizeLanguageCode(seriesId?.split('-')?.[0] || '');
      const candidateLanguages = Array.from(
        new Set([this.lang, fallbackLanguage].filter((value) => typeof value === 'string' && value.trim()))
      );

      let storiesSnapshot = null;
      let seriesSnapshot = null;
      for (const languageCode of candidateLanguages) {
        const storiesPath = `languages/${languageCode}/content/gradedReaders/${seriesId}/stories`;
        const seriesPath = `languages/${languageCode}/content/gradedReaders/${seriesId}`;

        const storiesRef = ref(rtdb, storiesPath);
        const candidateStoriesSnapshot = await get(storiesRef);
        if (candidateStoriesSnapshot.exists()) {
          storiesSnapshot = candidateStoriesSnapshot;
          // Best effort: series metadata is only used for level fallback.
          try {
            const candidateSeriesSnapshot = await get(ref(rtdb, seriesPath));
            if (candidateSeriesSnapshot.exists()) {
              seriesSnapshot = candidateSeriesSnapshot;
            }
          } catch {
            // Ignore metadata lookup failures.
          }
          break;
        }

        // Fallback for legacy payloads where stories are only read via full series object.
        const candidateSeriesSnapshot = await get(ref(rtdb, seriesPath));
        if (candidateSeriesSnapshot.exists()) {
          const candidateSeriesData = candidateSeriesSnapshot.val();
          if (candidateSeriesData?.stories) {
            seriesSnapshot = candidateSeriesSnapshot;
            break;
          }
        }
      }

      if (!storiesSnapshot?.exists() && !seriesSnapshot?.exists()) {
        return [];
      }

      const seriesData = seriesSnapshot?.val?.() || null;
      const storiesData = storiesSnapshot?.val?.() || seriesData?.stories;

      if (!storiesData) {
        return [];
      }

      const CEFR_ORDER = {
        A1: 1,
        A2: 2,
        B1: 3,
        B2: 4,
        C1: 5,
        C2: 6,
      };
      const getCefrRank = (value) => {
        if (!value) return Number.MAX_SAFE_INTEGER;
        const normalized = String(value).trim().toUpperCase();
        const directRank = CEFR_ORDER[normalized];
        if (directRank) return directRank;

        const match = normalized.match(/\b([ABC][12])\b/);
        if (match?.[1] && CEFR_ORDER[match[1]]) return CEFR_ORDER[match[1]];
        return Number.MAX_SAFE_INTEGER;
      };

      // Handle both array and object formats
      let stories = [];
      if (Array.isArray(storiesData)) {
        // Stories stored as array - use the story's internal 'id' field or index
        stories = storiesData.map((story, index) => ({
          ...story,
          id: story.id || String(index),
          seriesId,
          order: story.order !== undefined ? story.order : index,
          level: story.level || seriesData.level || null,
        }));
      } else if (typeof storiesData === 'object') {
        // Prefer snapshot iteration (mobile parity) when available.
        if (storiesSnapshot?.exists()) {
          stories = [];
          storiesSnapshot.forEach((childSnapshot) => {
            const childValue = childSnapshot.val();
            if (!childValue || typeof childValue !== 'object') return;
            stories.push({
              ...childValue,
              id: childValue.id || childSnapshot.key,
              seriesId,
              level: childValue.level || seriesData?.level || null,
            });
          });
        } else {
          // Stories stored as object with keys
          stories = Object.keys(storiesData).map(key => ({
            ...storiesData[key],
            id: storiesData[key].id || key,
            seriesId,
            level: storiesData[key].level || seriesData?.level || null,
          }));
        }
      }

      // Sort by CEFR level (A1 -> C2), then by order, then by id
      stories.sort((a, b) => {
        const levelRankA = getCefrRank(a.level);
        const levelRankB = getCefrRank(b.level);
        if (levelRankA !== levelRankB) return levelRankA - levelRankB;

        if (a.order !== undefined && b.order !== undefined) {
          return a.order - b.order;
        }

        if (a.order !== undefined) return -1;
        if (b.order !== undefined) return 1;

        return String(a.id).localeCompare(String(b.id));
      });

      return stories;
    }, { ttlMs: CACHE_TTL_MS.MEDIUM });
  }

  async resolveReaderStoryAudioUrl(story, seriesId) {
    const rawAudioUrl = typeof story?.audioUrl === 'string' ? story.audioUrl.trim() : '';
    const rawAudioPath = typeof story?.audioPath === 'string' ? story.audioPath.trim() : '';
    const candidate = rawAudioUrl || rawAudioPath;

    if (!candidate) return null;

    const storageInfo = extractStorageFileInfo(candidate, this.lang, seriesId);

    if (!storageInfo && isDirectUrl(candidate)) {
      return candidate;
    }

    let defaultBucketName = null;
    try {
      const { storage } = await import('../firebase');
      const { ref, getDownloadURL } = await import('firebase/storage');

      defaultBucketName = storage?.app?.options?.storageBucket || null;

      if (storage) {
        const candidateRefs = [storageInfo?.filePath].filter(Boolean);

        for (const sourceRef of Array.from(new Set(candidateRefs))) {
          try {
            const signedUrl = await getDownloadURL(ref(storage, sourceRef));
            if (signedUrl) return signedUrl;
          } catch {
            // Continue trying remaining source formats.
          }
        }
      }
    } catch {
      // Silently fall back to other URL resolution methods.
    }

    if (isDirectUrl(candidate)) {
      return candidate;
    }

    if (candidate.startsWith('audio/')) {
      return `/${candidate}`;
    }

    if (storageInfo?.filePath) {
      const bucketName = storageInfo.bucketName || defaultBucketName;
      if (bucketName) {
        return `https://storage.googleapis.com/${bucketName}/${encodeStoragePathForPublicUrl(storageInfo.filePath)}`;
      }
    }

    return candidate;
  }

  /**
   * Get a single story from a series
   * Stories are stored as an array, so storyId can be the index or the story's id field
   * @param {string} seriesId - e.g., 'es-a2-historias'
   * @param {string} storyId - The story's id field or array index
   * @returns {Promise<Object>} Full story object with content, vocabulary, comprehension
   */
  async getStory(seriesId, storyId) {
    const cacheKey = `${this.getCachePrefix()}:story:${seriesId}:${storyId}:v2`;
    return cachedRequest(cacheKey, async () => {
      const { rtdb } = await import('../firebase');
      const { ref, get } = await import('firebase/database');

      const fallbackLanguage = normalizeLanguageCode(seriesId?.split('-')?.[0] || '');
      const candidateLanguages = Array.from(
        new Set([this.lang, fallbackLanguage].filter((value) => typeof value === 'string' && value.trim()))
      );

      let snapshot = null;
      let dataPath = '';
      for (const languageCode of candidateLanguages) {
        dataPath = `languages/${languageCode}/content/gradedReaders/${seriesId}`;
        const seriesRef = ref(rtdb, dataPath);
        const candidateSnapshot = await get(seriesRef);
        if (candidateSnapshot.exists()) {
          snapshot = candidateSnapshot;
          break;
        }
      }

      if (!snapshot?.exists()) {
        console.error(`Series not found at path: ${dataPath}`);
        return null;
      }

      const seriesData = snapshot.val();
      const storiesData = seriesData.stories;

      if (!storiesData) {
        console.error(`No stories in series ${seriesId}`);
        return null;
      }

      // Find the story by id field or by index
      let story = null;

      if (Array.isArray(storiesData)) {
        // First try to find by the story's internal 'id' field
        // Use loose equality to handle numeric id vs string storyId from URL
        story = storiesData.find(s => s.id == storyId);
        // If not found, try by index
        if (!story && !isNaN(parseInt(storyId, 10))) {
          story = storiesData[parseInt(storyId, 10)];
        }
      } else if (typeof storiesData === 'object') {
        // First try direct key lookup
        story = storiesData[storyId];
        // If not found, search by internal 'id' field (handles case where
        // Firebase key differs from story's id field)
        // Use loose equality to handle numeric id vs string storyId from URL
        if (!story) {
          story = Object.values(storiesData).find(s => s && s.id == storyId);
        }
      }

      if (!story) {
        console.error(`[getStory] Story ${storyId} not found in series ${seriesId}`);
        return null;
      }

      const result = {
        ...story,
        id: story.id || storyId,
        seriesId
      };

      // Ensure vocabulary has matchForms (generate if missing)
      // Handle different field names: term/word, partOfSpeech/pos
      if (result.vocabulary && Array.isArray(result.vocabulary)) {
        result.vocabulary = result.vocabulary.map(vocab => {
          const term = vocab.term || vocab.word || '';
          const pos = vocab.partOfSpeech || vocab.pos || '';
          return {
            ...vocab,
            term: term, // Normalize to 'term'
            partOfSpeech: pos, // Normalize to 'partOfSpeech'
            matchForms: vocab.matchForms || (term ? this.generateMatchForms(term, pos) : [])
          };
        });
      }

      // Normalize story body fields from legacy and variant payload shapes.
      const normalizedParagraphs = normalizeParagraphList(result.paragraphs);
      if (!result.storyText && normalizedParagraphs.length > 0) {
        result.storyText = normalizedParagraphs.join('\n\n');
      }
      if (!result.storyText) {
        const fallbackBody = [
          result.markdownContent,
          result.content,
          result.body,
          result.text,
          result.articleText,
        ].find((value) => typeof value === 'string' && value.trim().length > 0);

        if (fallbackBody) {
          result.storyText = fallbackBody;
        }
      }
      if (!result.markdownContent && typeof result.storyText === 'string') {
        result.markdownContent = result.storyText;
      }

      // Normalize comprehension questions
      // Handle different field name formats:
      // - English: questions/answers
      // - Spanish: comprehensionQuestions/answerKey
      if (!result.comprehension) {
        const questions = result.questions || result.comprehensionQuestions;
        const answers = result.answers || result.answerKey || [];

        if (questions && Array.isArray(questions)) {
          // Combine questions and answers into comprehension format
          result.comprehension = questions.map((question, index) => ({
            question: question,
            answer: answers[index] || '',
            type: 'open' // Open-ended question format
          }));
        }
      }

      const rawAudioCandidate = result.audioUrl || result.audioPath || null;
      try {
        const resolvedAudioUrl = await Promise.race([
          this.resolveReaderStoryAudioUrl(result, seriesId),
          new Promise((resolve) => setTimeout(() => resolve(rawAudioCandidate), 1200)),
        ]);
        if (resolvedAudioUrl) {
          result.audioUrl = resolvedAudioUrl;
        }
      } catch {
        // Never block story rendering on audio resolution failures.
        if (rawAudioCandidate) {
          result.audioUrl = rawAudioCandidate;
        }
      }

      return result;
    }, { ttlMs: CACHE_TTL_MS.MEDIUM });
  }

  /**
   * Get reading progress for a specific story
   * @param {string} userId
   * @param {string} seriesId
   * @param {string} storyId
   * @returns {Promise<Object|null>} Progress object or null
   */
  async getStoryReadingProgress(userId, seriesId, storyId) {
    const cacheKey = `${this.getUserCachePrefix(userId)}:storyReadingProgress:${seriesId}:${storyId}`;
    return cachedRequest(cacheKey, async () => {
      const { db } = await import('../firebase');
      const { doc, getDoc } = await import('firebase/firestore');

      try {
        // Use composite key for story-level progress
        const progressId = `${seriesId}_${storyId}`;
        const progressRef = doc(db, 'users', userId, 'readingProgress', progressId);
        const snapshot = await getDoc(progressRef);

        if (!snapshot.exists()) {
          return null;
        }

        return {
          id: snapshot.id,
          ...snapshot.data()
        };
      } catch (error) {
        console.error('Error fetching story reading progress:', error);
        return null;
      }
    }, { ttlMs: CACHE_TTL_MS.SHORT });
  }

  /**
   * Update reading progress for a specific story
   * @param {string} userId
   * @param {string} seriesId
   * @param {string} storyId
   * @param {Object} progressData
   */
  async updateStoryReadingProgress(userId, seriesId, storyId, progressData) {
    const { db } = await import('../firebase');
    const { doc, setDoc, serverTimestamp } = await import('firebase/firestore');

    // Use composite key for story-level progress
    const progressId = `${seriesId}_${storyId}`;
    const progressRef = doc(db, 'users', userId, 'readingProgress', progressId);

    await setDoc(progressRef, {
      seriesId,
      storyId,
      language: this.lang,
      ...progressData,
      lastReadAt: serverTimestamp(),
      updatedAt: serverTimestamp()
    }, { merge: true });

    invalidateCacheByPrefix(this.getUserCachePrefix(userId));
  }

  /**
   * Get all series for current language
   * @param {Object} filters - { level, published, featured }
   * @returns {Promise<Array>} Array of series objects
   */
  async getAllReaderSeries(filters = {}) {
    const cacheKey = `${this.getCachePrefix()}:allReaderSeries:${stableSerialize(filters)}`;
    return cachedRequest(cacheKey, async () => {
      const { rtdb } = await import('../firebase');
      const { ref, get, query, orderByChild, equalTo } = await import('firebase/database');

      const dataPath = `languages/${this.lang}/content/gradedReaders`;

      try {
        const readersRef = ref(rtdb, dataPath);
        const normalizedLevel = normalizeCefrLevel(filters.level);
        const snapshot = await getReadersSnapshot(
          readersRef,
          normalizedLevel,
          { get, query, orderByChild, equalTo }
        );

        if (!snapshot.exists()) {
          return [];
        }

        const seriesList = [];
        snapshot.forEach((childSnapshot) => {
          try {
            const readerId = childSnapshot.key;
            const readerData = childSnapshot.val();
            if (!readerId || !readerData || typeof readerData !== 'object') return;
            // Allow cover-only collections (no stories) - they'll show "Coming Soon"

            const series = normalizeReaderSummary(readerId, readerData, this.lang);
            if (!series) return;
            if (!readerMatchesRequestedLevel(series.levelCodes || series.level, normalizedLevel)) return;

            if (filters.level && !normalizedLevel) {
              if (!readerSupportsLevel(series.levelCodes || series.level, filters.level, readerId)) return;
            }

            if (filters.published !== undefined) {
              const isPublished = series.published === true;
              if (filters.published === true && !isPublished) return;
              if (filters.published === false && isPublished) return;
            }

            if (filters.featured !== undefined && Boolean(series.featured) !== filters.featured) return;
            seriesList.push(series);
          } catch (itemError) {
            console.warn(
              `[LanguageService] Skipping malformed reader series at ${dataPath}/${childSnapshot.key}:`,
              itemError
            );
          }
        });

        // Sort by level, then title
        seriesList.sort((a, b) => {
          const rankDiff = getCefrSortRank(a.levelCodes || a.level) - getCefrSortRank(b.levelCodes || b.level);
          if (rankDiff !== 0) return rankDiff;
          return String(a.title || a.id).localeCompare(String(b.title || b.id));
        });

        return seriesList;
      } catch (error) {
        console.error(`[LanguageService] Error fetching reader series from ${dataPath}:`, error);
        return [];
      }
    }, { ttlMs: CACHE_TTL_MS.MEDIUM });
  }

  /**
   * Generate common word forms for vocabulary matching
   * Used as fallback when matchForms not pre-computed
   * @param {string} term - Base vocabulary term
   * @param {string} partOfSpeech - 'noun', 'verb', 'adjective', etc.
   * @returns {Array<string>} Array of possible word forms
   */
  generateMatchForms(term, partOfSpeech) {
    const forms = new Set([term.toLowerCase()]);
    const lowerTerm = term.toLowerCase();
    const vowels = 'aeiou';

    if (partOfSpeech === 'verb') {
      // Basic verb forms
      forms.add(lowerTerm + 's');
      forms.add(lowerTerm + 'ed');
      forms.add(lowerTerm + 'ing');

      // Handle -e ending (believe -> believing)
      if (lowerTerm.endsWith('e')) {
        const base = lowerTerm.slice(0, -1);
        forms.add(base + 'ing');
        forms.add(base + 'ed');
      }

      // Handle -y ending (rely -> relied)
      if (lowerTerm.endsWith('y') && !vowels.includes(lowerTerm.slice(-2, -1))) {
        const base = lowerTerm.slice(0, -1);
        forms.add(base + 'ies');
        forms.add(base + 'ied');
      }

      // Handle consonant doubling (stop -> stopping)
      const lastChar = lowerTerm.slice(-1);
      if (
        lowerTerm.length >= 3 &&
        !vowels.includes(lastChar) &&
        vowels.includes(lowerTerm.slice(-2, -1))
      ) {
        forms.add(lowerTerm + lastChar + 'ing');
        forms.add(lowerTerm + lastChar + 'ed');
      }
    } else if (partOfSpeech === 'noun') {
      // Basic plural
      forms.add(lowerTerm + 's');

      // Handle -y ending (berry -> berries)
      if (lowerTerm.endsWith('y') && !vowels.includes(lowerTerm.slice(-2, -1))) {
        forms.add(lowerTerm.slice(0, -1) + 'ies');
      }

      // Handle -s, -x, -ch, -sh endings
      if (/[sxz]$|[cs]h$/.test(lowerTerm)) {
        forms.add(lowerTerm + 'es');
      }

      // Handle -f/-fe endings (leaf -> leaves)
      if (lowerTerm.endsWith('f')) {
        forms.add(lowerTerm.slice(0, -1) + 'ves');
      }
      if (lowerTerm.endsWith('fe')) {
        forms.add(lowerTerm.slice(0, -2) + 'ves');
      }
    } else if (partOfSpeech === 'adjective') {
      // Comparative and superlative
      forms.add(lowerTerm + 'er');
      forms.add(lowerTerm + 'est');

      if (lowerTerm.endsWith('e')) {
        forms.add(lowerTerm + 'r');
        forms.add(lowerTerm + 'st');
      }

      if (lowerTerm.endsWith('y')) {
        const base = lowerTerm.slice(0, -1);
        forms.add(base + 'ier');
        forms.add(base + 'iest');
      }
    }

    return Array.from(forms);
  }

  // ============= GRADED READERS - DOWNLOADS =============

  /**
   * Generate secure download URLs for purchased content
   * @param {string} userId
   * @param {string} readerId
   * @param {string} fileType - 'epub' or 'audioZip'
   * @returns {Promise<string>} Signed URL
   */
  async generateDownloadUrl(userId, readerId, fileType) {
    const { getReaderFileAccess } = await import('./audioAccess');

    // Verify ownership first
    const hasAccess = await this.hasReaderAccess(userId, readerId);
    if (!hasAccess) {
      throw new Error('Access denied: User has not purchased this reader');
    }

    if (fileType !== 'epub' && fileType !== 'audioZip') {
      throw new Error('Unsupported fileType. Expected "epub" or "audioZip".');
    }

    const assetType = fileType === 'epub' ? 'epub' : 'audioZip';
    const { signedUrl: downloadUrl } = await getReaderFileAccess(
      readerId,
      assetType,
      this.lang
    );

    // Log the download
    await this.logDownload(userId, readerId, fileType);

    return downloadUrl;
  }

  /**
   * Log download activity
   * @param {string} userId
   * @param {string} readerId
   * @param {string} fileType
   * @returns {Promise<void>}
   */
  async logDownload(userId, readerId, fileType) {
    const { db } = await import('../firebase');
    const { doc, setDoc, increment, serverTimestamp } = await import('firebase/firestore');

    const downloadRef = doc(db, 'users', userId, 'downloads', readerId);

    const field = fileType === 'epub' ? 'epubDownloadedAt' : 'audioDownloadedAt';

    await setDoc(downloadRef, {
      readerId,
      [field]: serverTimestamp(),
      downloadCount: increment(1),
      lastDownloadedAt: serverTimestamp(),
      deviceInfo: navigator.userAgent
    }, { merge: true });
  }

  /**
   * Get user's download history
   * @param {string} userId
   * @param {string} readerId
   * @returns {Promise<Object|null>}
   */
  async getDownloadHistory(userId, readerId) {
    const { db } = await import('../firebase');
    const { doc, getDoc } = await import('firebase/firestore');

    try {
      const downloadRef = doc(db, 'users', userId, 'downloads', readerId);
      const snapshot = await getDoc(downloadRef);

      if (!snapshot.exists()) {
        return null;
      }

      return snapshot.data();
    } catch (error) {
      console.error('Error fetching download history:', error);
      return null;
    }
  }

  /**
   * Get streaming URL for audio chapter (for in-app player)
   * @param {string} userId
   * @param {string} readerId
   * @param {number} chapterId
   * @returns {Promise<string>}
   */
  async getAudioStreamUrl(userId, readerId, chapterId) {
    const { getReaderFileAccess } = await import('./audioAccess');

    // Verify ownership
    const hasAccess = await this.hasReaderAccess(userId, readerId);
    if (!hasAccess) {
      throw new Error('Access denied: Purchase required to access audio');
    }

    const { signedUrl } = await getReaderFileAccess(
      readerId,
      'chapterAudio',
      this.lang,
      chapterId
    );
    return signedUrl;
  }

  /**
   * Get EPUB URL for in-app reading
   * @param {string} userId
   * @param {string} readerId
   * @returns {Promise<string>}
   */
  async getEpubForReading(userId, readerId) {
    const { getReaderFileAccess } = await import('./audioAccess');

    // Verify ownership
    const hasAccess = await this.hasReaderAccess(userId, readerId);
    if (!hasAccess) {
      throw new Error('Purchase required to read this book');
    }

    const { signedUrl } = await getReaderFileAccess(readerId, 'epub', this.lang);
    return signedUrl;
  }

  // Additional methods would be implemented similarly using Realtime Database...
  // Truncating for brevity but following the same pattern
}

// Export singleton instances for common languages
export const englishService = new LanguageService('en');
export const spanishService = new LanguageService('es');

// Export the class for creating new instances
export default LanguageService;
